import React from 'react';
import '../styles/InterviewEffectiveness.css';

const InterviewEffectivenessRenderer = ({ effectivenessData }) => {
  if (!effectivenessData) return null;
  
  const { keySkills, interviewStructure, coverageGaps, effectivenessMetrics, recommendations } = effectivenessData;
  
  // Function to render inquiry depth
  const renderInquiryDepth = (depth) => {
    const maxDepth = 5;
    return (
      <div className="inquiry-depth">
        <div className="depth-bar-container">
          <div className="depth-bar" style={{ width: `${(depth / maxDepth) * 100}%`, backgroundColor: getDepthColor(depth) }}></div>
        </div>
        <span className="depth-number">{depth}/5</span>
      </div>
    );
  };
  
  // Function to get color based on depth
  const getDepthColor = (depth) => {
    if (depth >= 4) return '#4CAF50'; // Green
    if (depth >= 3) return '#8BC34A'; // Light green
    if (depth >= 2) return '#FFC107'; // Amber
    if (depth >= 1) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };
  
  return (
    <div className="interview-effectiveness-container">
      {/* Skills Coverage Section */}
      {keySkills && keySkills.length > 0 && (
        <section className="effectiveness-section skills-coverage">
          <h3>Skills Coverage Assessment</h3>
          <div className="skills-table">
            <div className="table-header">
              <div className="header-skill">Required Skill</div>
              <div className="header-assessed">Assessed</div>
              <div className="header-depth">Depth of Inquiry</div>
            </div>
            {keySkills.map((skill, index) => (
              <div className="skill-row" key={`skill-${index}`}>
                <div className="skill-name">{skill.skill}</div>
                <div className="skill-assessed">
                  <span className={`assessed-indicator ${skill.assessed ? 'yes' : 'no'}`}>
                    {skill.assessed ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="skill-depth">
                  {skill.assessed ? renderInquiryDepth(skill.depthOfInquiry) : <span className="not-assessed">N/A</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* Interview Structure Section */}
      {interviewStructure && (
        <section className="effectiveness-section interview-structure">
          <h3>Interview Structure Analysis</h3>
          <div className="structure-details">
            <div className="structure-distribution">
              <h4>Question Distribution</h4>
              <div className="distribution-chart">
                <div 
                  className="distribution-segment technical" 
                  style={{ width: `${interviewStructure.technicalAssessment}%` }}
                  title={`Technical: ${interviewStructure.technicalAssessment}%`}
                >
                  {interviewStructure.technicalAssessment}%
                </div>
                <div 
                  className="distribution-segment experience" 
                  style={{ width: `${interviewStructure.experienceAssessment}%` }}
                  title={`Experience: ${interviewStructure.experienceAssessment}%`}
                >
                  {interviewStructure.experienceAssessment}%
                </div>
                <div 
                  className="distribution-segment behavioral" 
                  style={{ width: `${interviewStructure.behavioralAssessment}%` }}
                  title={`Behavioral: ${interviewStructure.behavioralAssessment}%`}
                >
                  {interviewStructure.behavioralAssessment}%
                </div>
              </div>
              <div className="distribution-legend">
                <div className="legend-item">
                  <div className="legend-color technical"></div>
                  <span>Technical</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color experience"></div>
                  <span>Experience</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color behavioral"></div>
                  <span>Behavioral</span>
                </div>
              </div>
            </div>
            
            <div className="question-quality">
              <h4>Question Quality</h4>
              <div className="quality-rating">
                <div className="stars">
                  {Array(5).fill().map((_, i) => (
                    <span 
                      key={i} 
                      className={`star ${i < interviewStructure.questionQuality ? 'filled' : 'empty'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <span className="quality-score">{interviewStructure.questionQuality}/5</span>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Coverage Gaps Section */}
      {coverageGaps && coverageGaps.length > 0 && (
        <section className="effectiveness-section coverage-gaps">
          <h3>Coverage Gaps</h3>
          <p className="gaps-description">
            The following required skills or qualifications were not adequately covered during the interview:
          </p>
          <ul className="gaps-list">
            {coverageGaps.map((gap, index) => (
              <li key={`gap-${index}`}>{gap}</li>
            ))}
          </ul>
        </section>
      )}
      
      {/* Effectiveness Metrics Section */}
      {effectivenessMetrics && (
        <section className="effectiveness-section metrics">
          <h3>Effectiveness Metrics</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <h4>Skills Coverage Score</h4>
              <div className="metric-score" style={{ 
                color: effectivenessMetrics.skillsCoverageScore >= 75 ? '#4CAF50' : 
                      effectivenessMetrics.skillsCoverageScore >= 50 ? '#FF9800' : '#F44336'
              }}>
                {effectivenessMetrics.skillsCoverageScore}%
              </div>
            </div>
            
            <div className="metric-card">
              <h4>Question Distribution</h4>
              <div className="metric-stars">
                {Array(5).fill().map((_, i) => (
                  <span 
                    key={i} 
                    className={`star ${i < effectivenessMetrics.questionDistributionScore ? 'filled' : 'empty'}`}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>
            
            <div className="metric-card overall">
              <h4>Overall Effectiveness</h4>
              <div className="metric-score large" style={{ 
                color: effectivenessMetrics.overallEffectiveness >= 75 ? '#4CAF50' : 
                      effectivenessMetrics.overallEffectiveness >= 50 ? '#FF9800' : '#F44336'
              }}>
                {effectivenessMetrics.overallEffectiveness}%
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Recommendations Section */}
      {recommendations && recommendations.length > 0 && (
        <section className="effectiveness-section recommendations">
          <h3>Recommendations for Improvement</h3>
          <ul className="recommendations-list">
            {recommendations.map((recommendation, index) => (
              <li key={`rec-${index}`}>{recommendation}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default InterviewEffectivenessRenderer;